<script setup lang="ts">
import Button from '../design-system/button.vue';
import FileUpload from 'primevue/fileupload';
import { useAuthStore } from '~/stores/auth.store';
import { fileUploadPassthrough } from '~/components/passthroughs/fileupload';
import {
  ArrowUpOnSquareStackIcon,
  DocumentIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/vue/24/outline';
import GenericError from '~/components/user-interface/generic-error.vue';

interface IDocumentUpload {
  documentName: string;
  documentExtension: string;
  documentType: string;
  documentStatus: string;
  accountId: string;
  data: string | any;
}

interface IFileUploadProps {
  documentType: string | number;
  disabled?: boolean;
  currentFileCount?: number;
}

const { $t } = useNuxtApp();

const props = withDefaults(defineProps<IFileUploadProps>(), {
  disabled: false,
  currentFileCount: 0,
});

const { $complianceService } = useServices();
const auth = useAuthStore();
const site = useSiteStore();

const emits = defineEmits(['uploadFile', 'deleteFile', 'uploading']);

const totalSize = ref(0);
const totalSizePercent = ref(0);
const files = ref([]);
const docPreview = ref();

const onRemoveTemplatingFile = (file, removeFileCallback, index) => {
  removeFileCallback(index);
  totalSize.value -= parseInt(formatSize(file.size));
  totalSizePercent.value = totalSize.value / 10;
};

const clearFiles = (clearCallback: () => void) => {
  clearCallback();
  files.value = [];
};

const onSelectedFiles = (event) => {
  files.value = event.files;
  files.value.forEach((file) => {
    totalSize.value += parseInt(formatSize(file.size));
    docPreview.value = URL.createObjectURL(file);
  });
};

const uploadEvent = (callback) => {
  totalSizePercent.value = totalSize.value / 10;
  callback();
};

const formatSize = (bytes) => {
  const k = 1024;
  const dm = 1;
  // const sizes = this.$primevue.config.locale.fileSizeTypes;
  const sizes = ['b', 'kb', 'mb'];

  if (bytes === 0) {
    return `0 ${sizes[0]}`;
  }

  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const formattedSize = parseFloat((bytes / Math.pow(k, i)).toFixed(dm));

  return `${formattedSize} ${sizes[i]}`;
};

function splitFileByExt(fileName: string) {
  const regexArr = fileName?.match(/^(.*?)(?:\.(\w+))?$/);
  const [_, name, ext] = regexArr;
  return { fileName: name, fileExt: ext };
}

async function docToByteArr(uploadedFile: any) {
  const file = uploadedFile as File;
  //returning promise to async await reader.onload so files only get sent once data is converted
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = async function (f) {
      resolve(f.target.result.split(',')[1]);
    };
    reader.readAsDataURL(file);
  });
}

async function uploadFile() {
  emits('uploading', true);
  for (let i = 0; i < files.value.length; i++) {
    const file = files.value[i];
    const { fileName, fileExt } = splitFileByExt(file.name);
    const document: IDocumentUpload = {
      accountId: auth?.userId,
      documentName: fileName,
      documentType: String(props?.documentType),
      documentExtension: fileExt,
      documentStatus: 'AwaitingApproval',
      data: await docToByteArr(file),
    };

    await $complianceService.uploadDocument(document).then((data) => {
      //if successful
      if (data?.isSuccessful) {
        //call toast
        site.toggleSiteNotice({
          severity: 'success',
          heading: $t('success'),
          content: {
            data: `${$t('jpc-doc-ver-file')} ${file.name} ${$t('jpc-doc-ver-uploaded')}`,
          },
          timeout: 3000,
          enabled: true,
        });
        //emit file object to parent
        emits('uploadFile', data.data, file.objectURL);
      } else {
        site.toggleSiteNotice({
          severity: 'error',
          heading: $t('error'),
          content: { data: $t('unable-to-upload-file') },
          timeout: 3000,
          enabled: true,
        });
      }
      files.value = [];
    });
  }
  emits('uploading', false);
}

const disableSelect = computed(() => {
  if (props.disabled) return props.disabled;
  return props.currentFileCount + files.value.length >= 3;
});

const disableUpload = computed(() => {
  return props.currentFileCount + files.value.length > 3 || !files.value.length;
});
</script>

<template>
  <div class="card w-full">
    <GenericError v-if="currentFileCount === 3" class="m-4 w-full">
      {{ $t('document-verification-file-limit') }}
    </GenericError>
    <FileUpload
      :disabled="disableSelect"
      custom-upload
      accept="image/*,application/pdf,.doc,.docx,.odt,.fodt,.ott"
      :max-file-size="6000000"
      :file-limit="3 - currentFileCount"
      :pt="fileUploadPassthrough"
      @uploader="uploadFile($event)"
      @select="onSelectedFiles($event)"
    >
      <template
        #header="{ chooseCallback, uploadCallback, clearCallback, files }"
      >
        <hr class="w-full stacked-border-color mt-2" />
        <div class="flex flex-col sm:flex-row justify-between flex-1 gap-2 m-4">
          <Button
            type="tertiary"
            class="h-min px-4 justify-center"
            padding="md"
            :disabled="disableSelect"
            @click="chooseCallback()"
          >
            <PlusIcon class="w-6 mr-1" />
            {{ $t('choose-file') }}
          </Button>
          <div class="flex flex-col md:flex-row gap-2">
            <Button
              element-name="file-upload"
              :type="files.length > 0 ? 'primary' : 'tertiary'"
              :disabled="disableUpload"
              class="capitalize px-4 justify-center"
              @click="uploadEvent(uploadCallback)"
            >
              <ArrowUpOnSquareStackIcon class="w-6 mr-1" />
              {{ $t('upload') }}
            </Button>
            <Button
              element-name="file-upload-cancel"
              type="tertiary"
              :disabled="!files || files.length === 0"
              class="px-4 justify-center"
              @click="clearFiles(clearCallback)"
            >
              <XMarkIcon class="w-6 mr-1" />
              {{ $t('cancel') }}
            </Button>
          </div>
        </div>
      </template>
      <template #empty>
        <div
          v-if="currentFileCount < 3"
          class="flex-center flex-col h-full pb-2"
        >
          <DesignSystemIconsDragFileUploadIcon class="" />
          {{ $t('drag-and-drop-files') }}
        </div>
      </template>
      <template
        #content="{ files, removeFileCallback, removeUploadedFileCallback }"
      >
        <div v-if="files.length > 0" class="w-full">
          <div class="flex flex-wrap p-0 gap-1 px-2">
            <div
              v-for="(file, index) of files"
              :key="file.name + file.type + file.size"
              class="card m-0 w-full flex flex-col items-center gap-1"
            >
              <div
                class="flex upload-container py-1 bg-primary rounded-lg w-full mr-1/3"
              >
                <div class="m-2 flex justify-center w-6">
                  <img
                    v-if="file?.objectURL"
                    role="presentation"
                    :alt="file.name"
                    :src="file?.objectURL"
                    width="24"
                    height="24"
                    class="shadow-2"
                  />
                  <DocumentIcon v-else class="w-4" />
                </div>
                <div class="flex flex-col justify-center w-4/5">
                  <span class="font-semibold text-xs">
                    {{ file.name }} ({{ formatSize(file.size) }})
                  </span>
                  <span class="text-xs font-normal capitalize">
                    {{ $t('requested') }}:
                    {{
                      new Date()
                        .toLocaleDateString('en-GB')
                        .replaceAll('/', '-')
                    }}
                  </span>
                </div>
                <TrashIcon
                  class="w-4 cursor-pointer text-dark-700 dark:text-light-50 ml-auto content-center mr-2"
                  @click="
                    onRemoveTemplatingFile(file, removeFileCallback, index)
                  "
                />
              </div>
            </div>
          </div>
        </div>
      </template>
    </FileUpload>
  </div>
</template>

<style lang="scss">
.p-fileupload {
  &-buttonbar,
  &-content {
    @apply flex justify-center border-none;
    background-color: transparent;
  }
  &-file {
    @apply max-w-[150px] max-h-[150px];
  }
}

.p-fileupload-file {
  &-details,
  &-thumbnail {
    @apply text-dark-800 dark:text-light-50;
  }
}

.p-fileupload-content {
  @apply p-0;
}

div.p-fileupload-content div.upload-container {
  @apply flex !flex-row items-center w-full pl-2 !pr-4 text-xs border rounded-md border-light-400 dark:border-dark-500  text-dark-700 dark:text-light-50 dark:bg-dark-800;
}

span.p-button.p-component.p-fileupload-choose {
  @apply border border-light-400 bg-light-50 dark:border-dark-500 dark:bg-dark-800;
}
</style>
